<template>
  <div class="third-pay-config">
    <div
      v-for="groupItem in groupItems"
      :key="groupItem.key"
      class="group-card"
    >
      <h3 class="group-title">{{ groupItem.title }}</h3>

      <div
        v-for="platform in platformList"
        :key="groupItem.key + '-' + platform.key"
        class="platform-section"
      >
        <FormItem
          :label="platform.label + '开放规则：'"
          :label-width="120"
        >
          <div class="rule-table">
            <div class="rule-header">
              <span class="col-index">序号</span>
              <span class="col-entry">三方支付开放入口</span>
              <span class="col-action"></span>
            </div>
            <div
              v-for="(rule, index) in groupItem.config[platform.rulesKey]"
              :key="platform.key + '-rule-' + index"
              class="rule-row"
            >
              <span class="col-index">{{ index + 1 }}</span>
              <span class="col-entry">
                <Select
                  v-model="rule.entryConfigKeys"
                  multiple
                  filterable
                  placeholder="请选择开放入口"
                  :disabled="disabled"
                  :class="{
                    'select-error':
                      showRuleErrors && !rule.entryConfigKeys.length
                  }"
                >
                  <Option
                    v-for="item in entryOptions"
                    :key="
                      groupItem.key +
                      '-' +
                      platform.key +
                      '-entry-' +
                      buildEntryConfigKey(item.entrySourceType, item.entryId)
                    "
                    :value="
                      buildEntryConfigKey(item.entrySourceType, item.entryId)
                    "
                  >
                    {{ item.entryName }}
                  </Option>
                </Select>
                <span
                  v-if="showRuleErrors && !rule.entryConfigKeys.length"
                  class="error-tip"
                >
                  请选择开放入口
                </span>
              </span>
              <span class="col-action">
                <Icon
                  type="md-trash"
                  size="18"
                  color="#ed4014"
                  :style="{
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.5 : 1
                  }"
                  @click="removeRule(groupItem.config, platform, index)"
                />
              </span>
            </div>
          </div>
          <div
            v-if="!disabled"
            class="add-row"
            @click="addRule(groupItem.config, platform)"
          >
            + 新增规则
          </div>
        </FormItem>

        <FormItem label="支付方式配置：" :label-width="120">
          <Table
            :columns="methodColumns"
            :data="groupItem.config[platform.payMethodsKey]"
            border
            size="small"
            :draggable="!disabled"
            style="overflow: visible;"
            @on-drag-drop="
              (from, to) => onDrag(groupItem.config, platform, from, to)
            "
          >
            <template #sort>
              <span class="drag-handle">≡</span>
            </template>
            <template #method="{ index }">
              <Select
                v-model="
                  groupItem.config[platform.payMethodsKey][index].payMethod
                "
                size="small"
                placeholder="请选择"
                style="width: 100%"
                :disabled="
                  disabled ||
                  groupItem.config[platform.payMethodsKey][index].payMethod ===
                    0
                "
                @on-change="
                  (val) => onMethodChange(groupItem.config, platform, index, val)
                "
              >
                <Option
                  v-for="item in getMethodOptions(platform)"
                  :key="platform.key + '-method-' + item.dictValue"
                  :value="Number(item.dictValue)"
                  :disabled="
                    isMethodUsed(
                      groupItem.config,
                      platform,
                      Number(item.dictValue),
                      index
                    )
                  "
                >
                  {{ item.dictName }}
                </Option>
              </Select>
            </template>
            <template #provider="{ index }">
              <Select
                v-model="
                  groupItem.config[platform.payMethodsKey][index].provider
                "
                size="small"
                placeholder="--"
                style="width: 100%"
                :disabled="
                  disabled ||
                  groupItem.config[platform.payMethodsKey][index].payMethod ===
                    0
                "
              >
                <Option
                  v-for="item in getFilteredProviders(
                    groupItem.config,
                    platform,
                    index
                  )"
                  :key="platform.key + '-provider-' + item.dictValue"
                  :value="Number(item.dictValue)"
                  :disabled="
                    (isWalletSelected(groupItem.config, platform, index) &&
                      item.dictName !== 'PayPal') ||
                    (isAppleOrGooglePaySelected(
                      groupItem.config,
                      platform,
                      index
                    ) &&
                      item.dictName === 'PayPal')
                  "
                >
                  {{ item.dictName }}
                </Option>
              </Select>
            </template>
            <template #isDefault="{ index }">
              <Radio
                :disabled="disabled"
                :value="
                  groupItem.config[platform.payMethodsKey][index].isDefault
                "
                @on-change="setDefault(groupItem.config, platform, index)"
              ></Radio>
            </template>
            <template #action="{ index }">
              <Icon
                v-if="
                  groupItem.config[platform.payMethodsKey][index].payMethod !==
                    0
                "
                type="md-trash"
                size="18"
                color="#ed4014"
                :style="{
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: disabled ? 0.5 : 1
                }"
                @click="removeMethod(groupItem.config, platform, index)"
              />
            </template>
          </Table>
          <div
            v-if="
              !disabled &&
              groupItem.config[platform.payMethodsKey].length < maxMethodCount
            "
            class="add-row"
            @click="addMethod(groupItem.config, platform)"
          >
            + 新增支付方式
          </div>
        </FormItem>

        <FormItem label="支付链路：" :label-width="120">
          <RadioGroup
            v-model="groupItem.config[platform.payLinkKey]"
            type="button"
          >
            <Radio :label="0" :disabled="disabled">聚合支付</Radio>
            <Radio :label="1" :disabled="disabled">分步支付</Radio>
          </RadioGroup>
        </FormItem>
      </div>
    </div>
  </div>
</template>

<script>
import { getDictionaryList } from '@/api/dictionary'
import { getSwitchEntryOptions } from '@/api/thirdParty'
import { normalizeGroupParamByCapability } from '../helpers'

const PAGE_ENTRY_SOURCE_TYPE = 1
const ENTRY_CONFIG_KEY_SEPARATOR = '__'

const createEmptyRule = () => ({
  entryConfigKeys: []
})

const createExtraPayMethod = () => ({
  payMethod: '',
  provider: '',
  isDefault: false
})

export default {
  name: 'ThirdPayConfig',
  props: {
    formData: {
      type: Object,
      required: true
    },
    experimentGroupCount: {
      type: Number,
      default: 1
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      maxMethodCount: 6,
      showRuleErrors: false,
      platformList: [
        {
          key: 'ios',
          label: 'iOS',
          rulesKey: 'iosRules',
          payMethodsKey: 'iosPayMethods',
          payLinkKey: 'iosPayLinkType',
          methodOptionsKey: 'iosMethodOptions'
        },
        {
          key: 'android',
          label: '安卓',
          rulesKey: 'androidRules',
          payMethodsKey: 'androidPayMethods',
          payLinkKey: 'androidPayLinkType',
          methodOptionsKey: 'androidMethodOptions'
        }
      ],
      entryOptions: [],
      iosMethodOptions: [
        { dictValue: '0', dictName: '内购' },
        { dictValue: '1', dictName: '卡支付' },
        { dictValue: '2', dictName: 'Apple Pay' },
        { dictValue: '4', dictName: '钱包' }
      ],
      androidMethodOptions: [
        { dictValue: '0', dictName: '内购' },
        { dictValue: '1', dictName: '卡支付' },
        { dictValue: '3', dictName: 'Google Pay' },
        { dictValue: '4', dictName: '钱包' }
      ],
      providerOptions: [
        { dictValue: '0', dictName: 'Stripe' },
        { dictValue: '1', dictName: 'Antom' },
        { dictValue: '2', dictName: 'PayPal' }
      ]
    }
  },
  computed: {
    methodColumns() {
      return [
        { title: '排序', slot: 'sort', width: 48, align: 'center' },
        { title: '序号', type: 'index', width: 48, align: 'center' },
        { title: '支付方式', slot: 'method', minWidth: 110 },
        { title: '支付服务商', slot: 'provider', minWidth: 130 },
        { title: '设为默认', slot: 'isDefault', width: 82, align: 'center' },
        { title: '删除', slot: 'action', width: 52, align: 'center' }
      ]
    },
    groupItems() {
      const groups = Array.isArray(this.formData.groups)
        ? this.formData.groups
        : []

      return [
        {
          key: 'control',
          title: this.formData.controlGroupName || '对照组',
          config: this.formData.controlGroupParam
        },
        ...groups.map((group, index) => ({
          key: 'experiment-' + index,
          title: group.experimentGroupName || '实验组 ' + (index + 1),
          config: group.experimentGroupParam
        }))
      ].filter((item) => item.config)
    }
  },
  created() {
    this.syncAllGroupParams()
    this.fetchOptions()
  },
  methods: {
    getResponseList(response) {
      const data = response && response.data
      if (Array.isArray(data)) return data
      return data && Array.isArray(data.records) ? data.records : []
    },
    async fetchOptions() {
      try {
        const [entryRes] =
          await Promise.all([
            getSwitchEntryOptions(),
            getDictionaryList({ dictTopic: 'PAY_METHOD_TYPE_IOS' }),
            getDictionaryList({ dictTopic: 'PAY_METHOD_TYPE_ANDROID' }),
            getDictionaryList({ dictTopic: 'PAY_PROVIDER_TYPE' })
          ])

        this.entryOptions = this.getResponseList(entryRes).map((item) =>
          this.normalizeEntryOption(item)
        )
      } catch (error) {
        console.error('获取三方支付配置选项失败', error)
      }
    },
    normalizeEntrySourceType(entrySourceType) {
      const normalizedValue = Number(entrySourceType)
      return Number.isNaN(normalizedValue)
        ? PAGE_ENTRY_SOURCE_TYPE
        : normalizedValue
    },
    normalizeEntryOption(item = {}) {
      return {
        ...item,
        entrySourceType: this.normalizeEntrySourceType(item.entrySourceType),
        entryId:
          item.entryId === null || item.entryId === undefined
            ? ''
            : String(item.entryId)
      }
    },
    buildEntryConfigKey(entrySourceType, entryId) {
      return `${this.normalizeEntrySourceType(
        entrySourceType
      )}${ENTRY_CONFIG_KEY_SEPARATOR}${entryId}`
    },
    normalizeThirdPay(param) {
      return normalizeGroupParamByCapability('9', param)
    },
    syncAllGroupParams() {
      this.$set(
        this.formData,
        'controlGroupParam',
        this.normalizeThirdPay(this.formData.controlGroupParam)
      )

      const groups = this.formData.groups || []
      groups.forEach((group) => {
        this.$set(
          group,
          'experimentGroupParam',
          this.normalizeThirdPay(group.experimentGroupParam)
        )
      })
    },
    getMethodOptions(platform) {
      return this[platform.methodOptionsKey] || []
    },
    addRule(config, platform) {
      if (this.disabled) return
      this.showRuleErrors = false
      config[platform.rulesKey].push(createEmptyRule())
    },
    removeRule(config, platform, index) {
      if (this.disabled) return
      config[platform.rulesKey].splice(index, 1)
    },
    addMethod(config, platform) {
      if (this.disabled) return
      const list = config[platform.payMethodsKey]
      if (list.length >= this.maxMethodCount) {
        this.$Message.warning('最多添加6个支付方式')
        return
      }
      list.push(createExtraPayMethod())
    },
    removeMethod(config, platform, index) {
      if (this.disabled) return
      const list = config[platform.payMethodsKey]
      if (list[index] && list[index].payMethod === 0) {
        this.$Message.warning('内购支付方式不可删除')
        return
      }

      const wasDefault = list[index] && list[index].isDefault
      list.splice(index, 1)
      if (wasDefault && list.length > 0) {
        this.$set(list[0], 'isDefault', true)
      }
    },
    setDefault(config, platform, index) {
      if (this.disabled) return
      config[platform.payMethodsKey].forEach((item, itemIndex) => {
        this.$set(item, 'isDefault', itemIndex === index)
      })
    },
    isMethodUsed(config, platform, value, currentIndex) {
      return config[platform.payMethodsKey].some((item, index) => {
        return index !== currentIndex && item.payMethod === value
      })
    },
    onMethodChange(config, platform, index, value) {
      if (this.disabled) return
      const list = config[platform.payMethodsKey]
      const methodOptions = this.getMethodOptions(platform)

      if (value === 0) {
        this.$set(list[index], 'provider', '')
      } else {
        const walletOption = methodOptions.find(
          (item) => item.dictName === '钱包'
        )
        if (walletOption && value === Number(walletOption.dictValue)) {
          const paypalOption = this.providerOptions.find(
            (item) => item.dictName === 'PayPal'
          )
          if (paypalOption) {
            this.$set(list[index], 'provider', Number(paypalOption.dictValue))
          }
        } else {
          const targetOption = methodOptions.find(
            (item) =>
              item.dictName === 'Apple Pay' || item.dictName === 'Google Pay'
          )
          if (targetOption && value === Number(targetOption.dictValue)) {
            const paypalOption = this.providerOptions.find(
              (item) => item.dictName === 'PayPal'
            )
            if (
              paypalOption &&
              list[index].provider === Number(paypalOption.dictValue)
            ) {
              this.$set(list[index], 'provider', '')
            }
          }
        }
      }

      if (!list.some((item) => item.isDefault)) {
        this.$set(list[index], 'isDefault', true)
      }
    },
    isWalletSelected(config, platform, index) {
      const list = config[platform.payMethodsKey]
      const walletOption = this.getMethodOptions(platform).find(
        (item) => item.dictName === '钱包'
      )
      if (!walletOption || !list[index]) return false
      return list[index].payMethod === Number(walletOption.dictValue)
    },
    isAppleOrGooglePaySelected(config, platform, index) {
      const list = config[platform.payMethodsKey]
      const targetOption = this.getMethodOptions(platform).find(
        (item) => item.dictName === 'Apple Pay' || item.dictName === 'Google Pay'
      )
      if (!targetOption || !list[index]) return false
      return list[index].payMethod === Number(targetOption.dictValue)
    },
    getFilteredProviders(config, platform, index) {
      if (this.isAppleOrGooglePaySelected(config, platform, index)) {
        return this.providerOptions.filter((item) => item.dictName !== 'PayPal')
      }
      if (this.isWalletSelected(config, platform, index)) {
        return this.providerOptions.filter((item) => item.dictName === 'PayPal')
      }
      return this.providerOptions
    },
    onDrag(config, platform, from, to) {
      if (this.disabled || from === to || from < 0 || to < 0) return
      const list = config[platform.payMethodsKey]
      const moved = list.splice(from, 1)[0]
      list.splice(to, 0, moved)
    },
    validatePayMethods(payMethods, label) {
      if (!payMethods.length) {
        this.$Message.warning(`请至少配置一种${label}支付方式`)
        return false
      }

      let hasDefault = false
      for (const item of payMethods) {
        if (item.payMethod !== 0 && item.payMethod === '') {
          this.$Message.warning(`请选择${label}支付方式`)
          return false
        }
        if (item.payMethod !== 0 && item.provider === '') {
          this.$Message.warning(`请选择${label}支付服务商`)
          return false
        }
        if (item.isDefault) {
          hasDefault = true
        }
      }

      if (!hasDefault && payMethods.length) {
        this.$set(payMethods[0], 'isDefault', true)
      }

      return true
    },
    validate() {
      this.showRuleErrors = false

      for (const groupItem of this.groupItems) {
        for (const platform of this.platformList) {
          const rules = groupItem.config[platform.rulesKey] || []
          const hasEmptyRule = rules.some(
            (item) => !item.entryConfigKeys || !item.entryConfigKeys.length
          )
          if (hasEmptyRule) {
            this.showRuleErrors = true
            this.$Message.warning('请完成所有开放规则的填写')
            return false
          }

          const label = `${groupItem.title}${platform.label}`
          if (
            !this.validatePayMethods(
              groupItem.config[platform.payMethodsKey],
              label
            )
          ) {
            return false
          }
        }
      }

      return true
    }
  }
}
</script>

<style scoped lang="less">
.third-pay-config {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 30px;
  margin: 20px 0;
}

.group-card {
  min-width: 0;
  box-sizing: border-box;
  border: 1px solid #e8eaec;
  padding: 20px;
  border-radius: 4px;
}

.group-title {
  margin-bottom: 20px;
}

.platform-section + .platform-section {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px dashed #e8eaec;
}

.rule-table {
  border: 1px solid #e8eaec;
  border-radius: 4px;
  overflow: hidden;
}

.rule-header {
  display: flex;
  align-items: center;
  background: #f8f8f9;
  border-bottom: 1px solid #e8eaec;
  padding: 8px 0;
  font-size: 13px;
  font-weight: 600;
  color: #515a6e;
}

.rule-row {
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
}

.col-index {
  width: 50px;
  text-align: center;
  flex-shrink: 0;
}

.col-entry {
  flex: 1;
  padding: 0 8px;
}

.col-action {
  width: 40px;
  text-align: center;
  flex-shrink: 0;
}

.add-row {
  color: #2d8cf0;
  cursor: pointer;
  font-size: 13px;
  padding: 10px 0 0;

  &:hover {
    color: #57a3f3;
  }
}

.drag-handle {
  font-size: 16px;
  color: #999;
  cursor: grab;
  user-select: none;
}

.select-error /deep/ .ivu-select-selection {
  border-color: #ed4014 !important;
}

.error-tip {
  display: block;
  color: #ed4014;
  font-size: 12px;
  line-height: 1.2;
  padding-top: 4px;
}

@media (max-width: 1200px) {
  .third-pay-config {
    grid-template-columns: 1fr;
  }
}
</style>
